import LogoIcon from "../../../assets/icons/LogoIcon";
import UserSidebarIcon from "../../../assets/icons/UserSidebarIcon";
import ExitIcon from "../../../assets/icons/ExitIcon";
import MenuItem from "./MenuItem";
import newApi from "../../../services/newApi/api";
import type { IEnvelope } from "../../../services/newApi/typed";
import { toast } from "react-toastify";
import LayoutGridIcon from "../../../assets/icons/lucide/LayoutGridIcon";
import HouseLucideIcon from "../../../assets/icons/lucide/HouseLucideIcon";
import CalendarCheckLucideIcon from "../../../assets/icons/lucide/CalendarCheckLucideIcon";
import ClipboardListIcon from "../../../assets/icons/lucide/ClipboardListIcon";
import FilePlusIcon from "../../../assets/icons/lucide/FilePlusIcon";
import TriangleAlertIcon from "../../../assets/icons/lucide/TriangleAlertIcon";
import HeadsetIcon from "../../../assets/icons/lucide/HeadsetIcon";
import WorkflowIcon from "../../../assets/icons/lucide/WorkflowIcon";
import FolderPlusLucideIcon from "../../../assets/icons/lucide/FolderPlusLucideIcon";
import FolderOpenIcon from "../../../assets/icons/lucide/FolderOpenIcon";
import FileTextIcon from "../../../assets/icons/lucide/FileTextIcon";
import PenLineIcon from "../../../assets/icons/lucide/PenLineIcon";
import UsersLucideIcon from "../../../assets/icons/lucide/UsersLucideIcon";
import Building2Icon from "../../../assets/icons/lucide/Building2Icon";
import HistoryIcon from "../../../assets/icons/lucide/HistoryIcon";
import LogOutIcon from "../../../assets/icons/lucide/LogOutIcon";
import {
  AsideContainer,
  MainArea,
  SkipLink,
  AsideToggle,
  Container,
  Content,
  HeaderContainer,
  HeaderHospitalActive,
  HeaderLeftGroup,
  HeaderLogo,
  HeaderRightGroup,
  HeaderUser,
  MobileToggleButton,
  UserMenuAnchor,
  UserMenu,
  UserMenuHeader,
  UserMenuItem,
  ImpersonationBanner,
  BannerBtn,
} from "./style";

import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router";
import { type AsideItem, type IReport } from "./types";
import { useAuth } from "@features/auth";
import { useActiveHospital } from "@features/hospitals";
import { useQuery } from "@tanstack/react-query";
import { useReportModal } from "@features/reports";
import {
  PERMISSION_ADMINISTRATOR,
  PERMISSION_REPORTS,
  PERMISSION_RESULT,
  PERMISSION_SCHEDULING,
} from "../../../types/permissions";
import {
  hasHospitalScope,
  ROLE_CONVENIO,
  ROLE_DOCTOR,
  ROLE_HOSPITALS,
  ROLE_INTERNAL_DOCTOR,
  ROLE_OPERATION,
  ROLE_PATIENT,
} from "../../../types/role";
import { useHospitalList } from "@features/hospitals";
import { useActiveConvenio } from "../../../contexts/ActiveConvenio/useActiveConvenio";
import { useAuthFeatureFlag } from "../../../hooks/useAuthFeatureFlag";

import ChevronDownIcon from "../../../assets/icons/ChevronDownIcon";
import newTokenService from "../../../features/auth/services/newTokenService";
import loggedUserTitle from "./utils";
import { NotificationBell } from "../../NotificationBell";
import HospitalSelectDropdown from "../../../features/hospitals/context/ChangeActiveHospitalModal";
import { useSwitchUser } from "../../../features/auth/hooks/useSwitchUser";
import { holdsAnyPermissionCode } from "../../../features/auth/access/canAccess";
import { useIsImpersonating } from "../../../features/auth/hooks/useImpersonation";
import { SwitchUserModal } from "../../../features/auth/components/SwitchUserModal";
import { queryKeys } from "@lib/queryKeys";
import { ERP_PATHS, erpUrl } from "@lib/erpUrl";

const submitSsoForm = (url: string, fields: Record<string, string>) => {
  if (!url) {
    toast.error("URL do serviço não configurada. Contate o administrador.");
    return;
  }
  const form = document.createElement("form");
  form.method = "POST";
  form.action = url;
  form.target = "_blank";
  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

// O token é lido no clique, não na montagem do menu: o access token vive em memória e
// muda com refresh/rotação (ou chega depois do menu montado). Sem token, não posta o form.
const submitSsoFormWithCurrentToken = (
  url: string,
  fields: Record<string, string> = {},
) => {
  const token = newTokenService.getAccessToken();
  if (!token) {
    toast.error("Sessão não encontrada. Faça login novamente.");
    return;
  }
  submitSsoForm(url, { ...fields, token });
};

// Spec 20261028-130000 AC-7: one Lucide family, line, stroke 2, 20 px. The colour comes from the item (`currentColor`).
const menuIcon = { width: 20, height: 20, "aria-hidden": true } as const;

const getAsideItems = (
  auth: any,
  handleOpenReportModal: () => void,
  reports: AsideItem[],
  _setChangeActiveHospitalIsOpen: Dispatch<SetStateAction<boolean>>,
  agendamentoAmbulatorial?: boolean,
): AsideItem[] => {
  // Spec 20261028-130000 AC-8: day work, then management, then the ERP; Sair stays at the bottom. Only the order and
  // the `group` changed here: every permission and role gate below is the one the item had before.
  return [
    {
      content: "Início",
      icon: <HouseLucideIcon {...menuIcon} />,
      group: "day",
      // Home composta única; médico interno (role 5) mantém o formulário como entrada.
      href: auth.user?.role_id === 5 ? "/formulario-interno" : "/inicio",
    },
    {
      content: "Agendamento",
      href: undefined,
      icon: <CalendarCheckLucideIcon {...menuIcon} />,
      group: "day",
      permissions: [PERMISSION_SCHEDULING],
      role: (agendamentoAmbulatorial ? [ROLE_OPERATION, ROLE_PATIENT] : [ROLE_OPERATION]) as any,
      activeHospital: true,
      onClick: () => {
        const url = import.meta.env.VITE_APP_SCHEDULE_URL;
        if (!url) {
          toast.error("URL do serviço não configurada. Contate o administrador.");
          return;
        }
        window.location.href = url;
      },
    },
    {
      content: "Resultado",
      icon: <ClipboardListIcon {...menuIcon} />,
      group: "day",
      href: "/resultados",
      permissions: [PERMISSION_RESULT],
      role: [ROLE_OPERATION] as any,
    },
    {
      content: "Acréscimo",
      icon: <FilePlusIcon {...menuIcon} />,
      group: "day",
      href: "/acrescimo",
      // Paciente não vê nem cria acréscimo (dono, 2026-10-14; spec 20261013-112823).
      role: [ROLE_OPERATION, ROLE_HOSPITALS, ROLE_DOCTOR] as any,
    },
    {
      content: "Ocorrências",
      href: `/occurrence`,
      icon: <TriangleAlertIcon {...menuIcon} />,
      group: "day",
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Gestão Atendimento",
      href: undefined,
      icon: <HeadsetIcon {...menuIcon} />,
      group: "management",
      role: ROLE_OPERATION,
      activeHospital: true,
      onClick: () =>
        submitSsoFormWithCurrentToken(import.meta.env.VITE_APP_ATENDIMENTO_URL || "", {
          environment: "production",
        }),
    },
    {
      content: "NTO - Flow",
      href: undefined,
      icon: <WorkflowIcon {...menuIcon} />,
      group: "management",
      role: ROLE_OPERATION,
      activeHospital: true,
      onClick: () =>
        submitSsoFormWithCurrentToken(import.meta.env.VITE_APP_FLOW_URL || ""),
    },
    {
      content: "Novo Relatório",
      href: undefined,
      icon: <FolderPlusLucideIcon {...menuIcon} />,
      group: "management",
      onClick: () => handleOpenReportModal(),
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Relatório",
      href: undefined,
      icon: <FolderOpenIcon {...menuIcon} />,
      group: "management",
      permissions: [PERMISSION_REPORTS],
      dropdown: reports,
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      content: "Documentos",
      href: `/documentos`,
      icon: <FileTextIcon {...menuIcon} />,
      group: "management",
      permissions: [12],
    },
    {
      content: "Assinaturas",
      href: `/doctors-templates`,
      icon: <PenLineIcon {...menuIcon} />,
      group: "management",
      permissions: [12],
    },
    {
      content: "Usuários",
      href: "/users",
      icon: <UsersLucideIcon {...menuIcon} />,
      group: "management",
      permissions: [PERMISSION_ADMINISTRATOR],
      role: ROLE_OPERATION,
      activeHospital: true,
    },
    {
      // Spec 20261103-120000 AC-6: every unit, so no active hospital is needed.
      content: "Unidades",
      href: "/unidades",
      icon: <Building2Icon {...menuIcon} />,
      group: "management",
      role: ROLE_OPERATION,
    },
    // The Operação reads Observabilidade in the ERP; a hospital administrator keeps the access trail of their own
    // hospital here (spec 20261015-160924, "ERP + Acessos no exames").
    ...(auth.user?.role_id === ROLE_OPERATION
      ? []
      : [
        {
          content: "Observabilidade",
          href: "/observabilidade",
          icon: <HistoryIcon {...menuIcon} />,
          group: "management" as const,
          // O mesmo direito que a rota e o servidor exigem (P20EE-188).
          permissionCodes: ["audit:read"],
          activeHospital: true,
        },
      ]),
    {
      // Spec 20261015-160924 T12: Carlão (Configuração, Analytics) and Observabilidade live in the ERP, in
      // portal-senneliquor. Only the Operação enters the ERP, and every Operação person sees at least the Grade there.
      content: "ERP",
      href: undefined,
      // Spec 20261028-130000 AC-7 (D6): the legacy 47 px SVG had `fill="none"` and drew nothing in this menu.
      icon: <LayoutGridIcon {...menuIcon} data-testid="erp-menu-icon" />,
      group: "erp",
      external: true,
      role: ROLE_OPERATION,
      onClick: () => {
        const url = erpUrl(import.meta.env.VITE_APP_SCHEDULE_URL, ERP_PATHS.home);
        if (!url) {
          toast.error("URL do serviço não configurada. Contate o administrador.");
          return;
        }
        window.location.href = url;
      },
    },
    {
      content: "Sair",
      // SEM `href` — MenuItem só dispara onClick quando href ausente.
      // Antes `href: "#exit"` fazia o item virar <Link> e ignorar onClick → URL ganhava #exit, logout não rodava.
      icon: <LogOutIcon {...menuIcon} />,
      onClick: () => auth.logout(),
      classname: "--logout_bottom"
    },
  ];
};

/** Marks the first visible item of each group after the permission filter, so a separator never opens the list. */
const markGroupStarts = (items: AsideItem[]): AsideItem[] =>
  items.map((item, index) =>
    index > 0 && item.group && item.group !== items[index - 1]?.group ? { ...item, groupStart: true } : item,
  );

const formatReportToAside = (reports: IReport[]): AsideItem[] => {
  return reports.map((report) => ({
    content: report.label ?? report.name,
    href: `/relatorio/${report.id}`,
  }));
};

const Aside = ({ children, customPlaceholder }: { children: ReactNode; customPlaceholder?: ReactNode }) => {
  const activeHospital = useActiveHospital();
  const activeConvenio = useActiveConvenio();
  const reportModal = useReportModal();

  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const asideRef = useRef<HTMLElement>(null) as any;
  const listReportsRef = useRef<AbortController | null>(null);
  const mobileToggleRef = useRef<HTMLDivElement>(null);
  const hospitalSelectRef = useRef<HTMLDivElement>(null);

  // Padrão = rail colapsado de 76px (fiel ao DC); o hambúrguer expande p/ ver labels.
  const [asideIsOpen, setAsideIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [switchModalOpen, setSwitchModalOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const { switchBack, isLoading: switchLoading } = useSwitchUser();
  // Estado autoritativo: claim `impersonatorId` do JWT (não sessionStorage),
  // via subscribe do token service. Ler no render deixava o banner sumir quando
  // o token chegava DEPOIS da montagem (reload/reidratação) — personificação
  // silenciosa e sem saída (G14).
  const isImpersonating = useIsImpersonating();

  // O portão é o mesmo do backend (`isAdminOrHospital`). Antes era `!== ROLE_CONVENIO`, que
  // deixava passar paciente e médico — e passava também no primeiro render, quando o papel ainda
  // não chegou, porque lista de exclusão é verdadeira para `undefined`.
  useHospitalList(Number(auth.user?.id), hasHospitalScope(auth.user?.role_id));

  const { enabled: agendamentoAmbulatorial } = useAuthFeatureFlag("agendamento_ambulatorial");

  const { data: reports } = useQuery({
    queryKey: queryKeys.reports.list(auth.user?.role_id, activeHospital.uuid),
    queryFn: async () => {
      listReportsRef.current?.abort();
      listReportsRef.current = new AbortController();
      try {
        const response = await newApi.get<IEnvelope<{ reports: IReport[] }>>("/reports", {
          // P20EE-206: only `uuid` goes, confronted with the token by the backend (assertAccessScope).
          // `userId` and `permissions` came from storage the client can edit, and ListReportsController
          // already derives both from the token, so sending them authorized nothing (senneliquor: P20EE-160).
          params: { uuid: activeHospital.uuid },
          signal: listReportsRef.current.signal,
        });
        return response.data.data.reports;
      } catch (err) {
        // 403 aqui não é falha: é "esta pessoa não tem relatório NESTE escopo" — o backend
        // gateia por hospital ativo. Antes virava erro de query e um `console.error` mudo:
        // o item de menu sumia sem nada dizer, duas vezes por carga.
        if ((err as { response?: { status?: number } })?.response?.status === 403) return [];
        throw err;
      }
    },
    enabled: (!!activeHospital.uuid || auth.user?.role_id === ROLE_OPERATION) && auth.user?.role_id !== 3,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 6 * 60 * 60 * 1000,
  });

  const userPermissions = auth.user?.permissoes?.map((item: any) => {
    if (Number.isInteger(item)) return item;
    return Number(item.id);
  });

  const userInitials =
    (auth.user?.name ?? "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((n: string) => n[0])
      .join("")
      .toUpperCase() || "?";

  const menuItems = useMemo(() => {
    const allMenuItems = getAsideItems(
      auth,
      () => reportModal.setUpdateReportModalIsOpen(true),
      formatReportToAside(reports ?? []),
      activeHospital.setChangeActiveHospitalIsOpen,
      agendamentoAmbulatorial,
    ).filter((item) => {
      // Guard: feature flag blocks ROLE_PATIENT from Agendamento regardless of permissions
      if (
        auth.user?.role_id === ROLE_PATIENT &&
        item.content === "Agendamento" &&
        !agendamentoAmbulatorial
      ) {
        return false;
      }

      if (auth.user?.role_id === ROLE_CONVENIO) {
        if (item.classname === "--logout_bottom") return true; // Sair
        if (item.href === "/resultados") return true;
        if (item.href === "/users") return !!(item.permissions?.some((p) => userPermissions?.includes(p)));
        return false;
      }
      if (
        auth.user === null ||
        (auth.user?.role_id === ROLE_HOSPITALS &&
          activeHospital.isProcedenciaActive === false)
      ) {
        return item.content === "Início" || item.content === "Sair";
      }
      if (item.permissionCodes) return holdsAnyPermissionCode(auth.user, item.permissionCodes);
      return (
        (!item.permissions && !item.role) ||
        item.permissions?.some((permission) =>
          userPermissions?.includes(permission),
        ) ||
        (Array.isArray(item.role) ? item.role : [item.role])?.includes(
          auth.user?.role_id as any,
        )
      );
    });

    const logoutItem = allMenuItems.find((item) => item.classname === "--logout_bottom");
    const otherItems = allMenuItems.filter((item) => item.classname !== "--logout_bottom");

    return { otherItems: markGroupStarts(otherItems), logoutItem };
  }, [
    auth,
    activeHospital.permissions,
    activeHospital.uuid,
    location.pathname,
    reportModal,
    reports,
    activeHospital.isProcedenciaActive,
    activeHospital.setChangeActiveHospitalIsOpen,
    userPermissions,
    agendamentoAmbulatorial,
  ]);

  useEffect(() => {
    const handleAsideClickOutside = (e: MouseEvent) => {
      if (
        window.innerWidth <= 768 &&
        asideRef.current &&
        mobileToggleRef.current &&
        !asideRef.current.contains(e.target as Node) &&
        !mobileToggleRef.current.contains(e.target as Node)
      ) {
        setAsideIsOpen(false);
      }
    };

    window.addEventListener("click", handleAsideClickOutside);

    return () => window.removeEventListener("click", handleAsideClickOutside);
  }, []);

  useEffect(() => {
    window.innerWidth <= 768 && setAsideIsOpen(false);
  }, [location.pathname]);

  // Fecha o dropdown de hospital ao clicar fora do conjunto pill + painel.
  useEffect(() => {
    if (!activeHospital.changeActiveHospitalIsOpen) return;
    const handler = (e: MouseEvent) => {
      if (
        hospitalSelectRef.current &&
        !hospitalSelectRef.current.contains(e.target as Node)
      ) {
        activeHospital.setChangeActiveHospitalIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [activeHospital.changeActiveHospitalIsOpen, activeHospital.setChangeActiveHospitalIsOpen]);

  // Fecha o menu do avatar ao clicar fora.
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [userMenuOpen]);

  return (
    <Container>
      <SkipLink href="#conteudo">Pular para o conteúdo</SkipLink>

      {/* Mobile toggle button — always visible */}
      <MobileToggleButton
        $isOpen={asideIsOpen}
        ref={mobileToggleRef}
        onClick={() => setAsideIsOpen((prev) => !prev)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 50 50">
          <path d="M 5 8 A 2.0002 2.0002 0 1 0 5 12 L 45 12 A 2.0002 2.0002 0 1 0 45 8 L 5 8 z M 5 23 A 2.0002 2.0002 0 1 0 5 27 L 45 27 A 2.0002 2.0002 0 1 0 45 23 L 5 23 z M 5 38 A 2.0002 2.0002 0 1 0 5 42 L 45 42 A 2.0002 2.0002 0 1 0 45 38 L 5 38 z"></path>
        </svg>
      </MobileToggleButton>

      {/* `as="nav"`: o menu é o landmark de navegação da área logada. Como `aside` ele saía como
          "complementar", e a tela não tinha nenhum landmark de navegação. */}
      <AsideContainer as="nav" aria-label="Menu principal" $isOpen={asideIsOpen} ref={asideRef}>
        <HeaderLogo $isOpen={asideIsOpen}>
          <img className="mark" src="/logo512.png" alt="Senne" />
          <div className="icon">
            <LogoIcon />
          </div>
          <AsideToggle
            $isOpen={asideIsOpen}
            onClick={() => setAsideIsOpen((prev) => !prev)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="25" height="25" viewBox="0 0 50 50">
              <path d="M 5 8 A 2.0002 2.0002 0 1 0 5 12 L 45 12 A 2.0002 2.0002 0 1 0 45 8 L 5 8 z M 5 23 A 2.0002 2.0002 0 1 0 5 27 L 45 27 A 2.0002 2.0002 0 1 0 45 23 L 5 23 z M 5 38 A 2.0002 2.0002 0 1 0 5 42 L 45 42 A 2.0002 2.0002 0 1 0 45 38 L 5 38 z"></path>
            </svg>
          </AsideToggle>
        </HeaderLogo>
        <MenuItem asideItems={menuItems.otherItems} pathname={location.pathname} />
        {customPlaceholder && asideIsOpen && customPlaceholder}
        {menuItems.logoutItem && <MenuItem asideItems={[menuItems.logoutItem]} pathname={location.pathname} />}
      </AsideContainer>

      <Content $isOpen={asideIsOpen}>
        {isImpersonating && (
          <ImpersonationBanner>
            <span>
              Você está vendo como <b>{auth.user?.name}</b>
            </span>
            <BannerBtn
              type="button"
              onClick={() => switchBack()}
              disabled={switchLoading}
            >
              Voltar à minha sessão
            </BannerBtn>
          </ImpersonationBanner>
        )}
        <HeaderContainer>
          <HeaderLeftGroup>
            {auth.user?.role_id === ROLE_CONVENIO ? (
              <HeaderHospitalActive $noEdit>
                <span>Convênio ativo: {activeConvenio.ds_convenio || "—"}</span>
              </HeaderHospitalActive>
            ) : (
              hasHospitalScope(auth.user?.role_id) &&
              auth.user?.status !== 5 &&
              (() => {
                // A condição de "pode trocar" estava escrita duas vezes, no `onClick` e no
                // `$noEdit`, e as duas podiam divergir sem nada acusar.
                const podeTrocarHospital =
                  auth.user?.status !== 5 && auth.user?.role_id !== ROLE_INTERNAL_DOCTOR;
                return (
                <div ref={hospitalSelectRef} style={{ position: "relative" }}>
                  {/* `button` quando troca, `div` quando não. Era sempre `div` com `onClick`: abria
                      um seletor e não se anunciava como controle, então não existia para teclado
                      nem para leitor de tela. Achado na validação funcional por papel de
                      2026-09-10 — o localizador por papel do harness não achava, pelo mesmo motivo
                      que uma pessoa usando leitor de tela não acharia. */}
                  <HeaderHospitalActive
                    as={podeTrocarHospital ? "button" : "div"}
                    type={podeTrocarHospital ? "button" : undefined}
                    aria-haspopup={podeTrocarHospital ? "dialog" : undefined}
                    aria-expanded={
                      podeTrocarHospital
                        ? activeHospital.changeActiveHospitalIsOpen
                        : undefined
                    }
                    onClick={
                      podeTrocarHospital
                        ? () =>
                            activeHospital.setChangeActiveHospitalIsOpen((prev) => !prev)
                        : undefined
                    }
                    $noEdit={!podeTrocarHospital}
                  >
                    <span>
                      Hospital ativo: {activeHospital.name || "Nenhum selecionado"}
                      <span className="icon" style={{ marginLeft: "6px", display: "inline-flex" }}>
                        <ChevronDownIcon />
                      </span>
                    </span>
                  </HeaderHospitalActive>
                  <HospitalSelectDropdown
                    changeActiveHospitalIsOpen={activeHospital.changeActiveHospitalIsOpen}
                    setChangeActiveHospitalIsOpen={activeHospital.setChangeActiveHospitalIsOpen}
                    activeHospital={activeHospital}
                    setActiveHospital={activeHospital.setActiveHospital}
                  />
                </div>
                );
              })()
            )}
          </HeaderLeftGroup>
          <HeaderRightGroup>
            {auth.user?.role_id !== ROLE_CONVENIO && <NotificationBell />}
            <UserMenuAnchor ref={userMenuRef}>
              <HeaderUser as="button" type="button" onClick={() => setUserMenuOpen((prev) => !prev)}>
                <div className="header_user_image">{userInitials}</div>
                <div className="header_user_content">
                  <div className="header_user_content_name">{auth.user?.name}</div>
                  <div className="header_user_role">{loggedUserTitle(auth.user?.role_id || 0)}</div>
                </div>
                <ChevronDownIcon />
              </HeaderUser>
              {userMenuOpen && (
                <UserMenu role="menu">
                  <UserMenuHeader>
                    <div className="name">{auth.user?.name}</div>
                    <div className="role">{loggedUserTitle(auth.user?.role_id || 0)}</div>
                  </UserMenuHeader>
                  {auth.user?.role_id === ROLE_OPERATION && !isImpersonating && (
                    <UserMenuItem
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        setSwitchModalOpen(true);
                      }}
                    >
                      <UserSidebarIcon />
                      Ver como outro usuário
                    </UserMenuItem>
                  )}
                  {!isImpersonating && (
                    <UserMenuItem
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setUserMenuOpen(false);
                        navigate("/meu-perfil");
                      }}
                    >
                      <UserSidebarIcon />
                      Meu perfil
                    </UserMenuItem>
                  )}
                  <UserMenuItem type="button" role="menuitem" onClick={() => auth.logout()}>
                    <ExitIcon />
                    Sair
                  </UserMenuItem>
                </UserMenu>
              )}
            </UserMenuAnchor>
          </HeaderRightGroup>
        </HeaderContainer>
        {switchModalOpen && (
          <SwitchUserModal onClose={() => setSwitchModalOpen(false)} />
        )}
        <MainArea id="conteudo">{children}</MainArea>
      </Content>
    </Container>
  );
};

export default Aside;
